import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, interval } from 'rxjs';
import {
  takeUntil,
  debounce,
  distinctUntilChanged,
  take,
} from 'rxjs/operators';
import { PotreeCesiumViewerService } from 'src/app/components/potree-cesium-viewer/potree-cesium-viewer.service';
import { dataURLtoFile } from 'src/app/helpers/file.helper';
import { LS } from 'src/app/helpers/localStorage.enum';
import { AnnotationService } from 'src/app/services/annotation/annotation.service';
import { AuditTrailService } from 'src/app/services/audit-trail/audit-trail.service';
import { FileService } from 'src/app/services/file/file.service';
import { GroupService } from 'src/app/services/group/group.service';
import { LocationUIService } from 'src/app/services/location-ui/location-ui.service';
import { LocationService } from 'src/app/services/location/location.service';
import { LocationComponentService } from 'src/app/services/locationComponent/location-component.service';
import { MapLayerService } from 'src/app/services/map-layer/map-layer.service';
import { MapService } from 'src/app/services/map/map.service';
import { MetadataService } from 'src/app/services/metadata/metadata.service';
import { SwalService } from 'src/app/services/swal/swal.service';
import { UserService } from 'src/app/services/user/user.service';
import { GenericTypeStoreService } from 'src/app/stores/generic-types-store/generic-type-store.service';
import { LayerStoreService } from 'src/app/stores/layer-store/layer-store.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';

@Component({
  selector: 'app-info',
  templateUrl: './info.component.html',
  styleUrls: ['./info.component.sass'],
})
export class DetailsComponent implements OnInit, OnDestroy {
  currLocation;
  components = [];
  mapLayers = [];
  annotations;
  formComponent: FormGroup;
  formMapLayer: FormGroup;
  formMembers: FormGroup;
  formReviewers: FormGroup;
  formSubmitters: FormGroup;
  isShowFormComp: boolean = false;
  isShowFormMap: boolean = false;
  isShowFormMember: boolean = false;
  isShowFormReviewer: boolean = false;
  isShowFormSubmitter: boolean = false;
  componentTypes;
  permissions;
  mapScreenshot: any;
  allMembers;
  allReviewer = [];
  allSubmitter = [];
  ui: {
    files: {
      file: File;
      name: string;
      uploadStatus: string;
    }[];
  } = {} as any;
  progress = -1;
  taskForm: FormGroup = this.formBuilder.group({
    folder: ['Raw images'],
  });
  memberSelection;
  countByAssignedRole;
  unsubber: Subject<boolean> = new Subject<boolean>();
  /** If changes is incremented, currentLocation will be refreshed */
  changes: number = 0;
  memberSelected: any;
  reviewerSelected: any;
  isAllAssets = true;
  roles: any;
  isFacility = false;

  terms$ = new Subject<any>();
  locAdditionalData: any;
  review_module_enabled = false;

  constructor(
    private router: Router,
    private locationService: LocationService,
    private locationCompService: LocationComponentService,
    private mapLayerService: MapLayerService,
    private spinner: NgxSpinnerService,
    private formBuilder: FormBuilder,
    private mapService: MapService,
    private potreeCesiumViewerService: PotreeCesiumViewerService,
    private locUIService: LocationUIService,
    private swalService: SwalService,
    private fileService: FileService,
    private annotService: AnnotationService,
    private userService: UserService,
    private groupService: GroupService,
    private metadataService: MetadataService,
    private layerStore: LayerStoreService,
    private auditTrailService: AuditTrailService,
    private _t: TranslateService,
    private toastr: ToastrService,
    private locationStore: LocationStoreService,
    private genericTypeStore: GenericTypeStoreService
  ) {}

  ngOnInit(): void {
    this.permissions = JSON.parse(
      localStorage.getItem(LS.currentUser)
    ).permissions;
    this.roles = JSON.parse(localStorage.getItem(LS.currentUser)).roles;
    this.formComponent = this.formBuilder.group({
      name: ['', Validators.required],
      component_type: ['', Validators.required],
      location: ['', Validators.required],
    });
    this.formMapLayer = this.formBuilder.group({
      external_url: ['', Validators.required],
      layer_type: ['', Validators.required],
      location: ['', Validators.required],
      layer_name: [''],
      layer_date: [new Date().toISOString().substring(0, 10)],
    });
    this.formMembers = this.formBuilder.group({
      members: ['', Validators.required],
    });
    this.formReviewers = this.formBuilder.group({
      reviewers: ['', Validators.required],
    });
    this.formSubmitters = this.formBuilder.group({
      submitters: ['', Validators.required],
    });
    this.locationStore.currLocation$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe(async (res) => {
        if (res.id) {
          this.currLocation = res;
          this.mapScreenshot = this.currLocation.location_thumbnail;
          this.formComponent.patchValue({ location: this.currLocation.id });
          this.formMapLayer.patchValue({ location: this.currLocation.id });
          this.locationCompService
            .findAll([
              { key: 'filtered[0][key]', val: 'location' },
              { key: 'filtered[0][val]', val: this.currLocation.id },
            ])
            .subscribe((res) => {
              this.components = res.data;
            });
          this.mapLayerService
            .findAll([
              { key: 'filtered[0][key]', val: 'location' },
              { key: 'filtered[0][val]', val: this.currLocation.id },
            ])
            .subscribe((res) => {
              this.mapLayers = res.data;
            });

          this.annotService
            .findAll([
              { key: 'filtered[0][key]', val: 'location' },
              { key: 'filtered[0][val]', val: this.currLocation.id },
            ])
            .subscribe((res) => {
              this.annotations = res.data;
            });

          // getting all user
          if (this.currLocation.members || this.currLocation.groups) {
            this.allMembers = [
              ...this.currLocation.members.map((i) => {
                return { name: i.fullname, description: i.email, id: i.id };
              }),
              ...this.currLocation.groups,
            ];
          } else {
            const members = await this.locationService
              .getMembers(this.currLocation.id)
              .toPromise();
            this.allMembers = members.allMembers;
            res.members = members.members;
            res.groups = members.groups;
          }

          if (this.currLocation.reviewers) {
            this.allReviewer =
              this.currLocation.reviewers.length > 0
                ? this.currLocation.reviewers.map((i) => {
                    return { name: i.fullname, description: i.email, id: i.id };
                  })
                : [];
          }

          if (this.currLocation.submitter) {
            this.allSubmitter =
              this.currLocation.submitter.length > 0
                ? this.currLocation.submitter.map((i) => {
                    return { name: i.fullname, description: i.email, id: i.id };
                  })
                : [];
          }

          let objMember = {};
          const members = [
            ...res.members,
            ...res.groups.reduce((acc, i) => [...acc, ...i.members], []),
          ];
          members.forEach((i) => {
            objMember[i.id] = i;
          });
          let newMembers = [];
          for (let key in objMember) {
            let om = objMember[key];
            if (!om.roles) {
              om.roles = [];
            }
            newMembers.push(om);
          }
          /**
            {
              count: 18
              _id: "Admin"
            }
           */
          const roles = newMembers.reduce((acc, i) => [...acc, ...i.roles], []);
          this.countByAssignedRole = Object.values(
            roles.reduce((c, v) => {
              c[v] = c[v] || [v, 0];
              c[v][1]++;
              return c;
            }, {})
          ).map((item) => ({ _id: item[0], count: item[1] }));
          if (this.roles[0] !== 'SuperAdmin') {
            this.countByAssignedRole = [];
          }
          if (res.additional_data_json) {
            this.locAdditionalData = JSON.parse(res.additional_data_json);
          }

          if (this.currLocation.children?.length) this.isFacility = true;
          this.review_module_enabled = res.show_review_module
            ? res.show_review_module
            : false;
        }
      });

    this.terms$
      .pipe(
        debounce(() => interval(500)),
        distinctUntilChanged()
      )
      .subscribe((term) => {
        const { key, includeGroup } = term;
        this.searchMember(key, includeGroup);
      });
  }

  onEdit() {
    this.router.navigate(['/projects', this.currLocation.id, 'edit']);
  }

  undoMsg = this._t.instant('Tap HERE to undo');

  onDelete() {
    this.swalService.confirmDialog((swalRes) => {
      this.spinner.show();
      this.locationService
        .remove(this.currLocation.id)
        .pipe(takeUntil(this.unsubber.asObservable()))
        .subscribe((res) => {
          if (res.parent && res.parent.length > 0) {
            this.router.navigate(['projects', res.parent, 'details']);
          } else {
            this.router.navigate(['projects']);
          }
          this.spinner.hide();
          this.toastr
            .info(this.undoMsg, '', {
              progressBar: true,
              timeOut: 7000,
              enableHtml: true,
            })
            .onTap.pipe(take(1))
            .subscribe(() => {
              this.locationService.restore(this.currLocation.id).subscribe();
            });
        });
    });
  }

  onDeleteComponent(id, key) {
    this.spinner.show();
    this.annotService
      .findAll([
        { key: 'filtered[0][key]', val: 'location_component' },
        { key: 'filtered[0][val]', val: id },
        { key: 'limit', val: 1 },
        { key: 'select[0]', val: 'id' },
      ])
      .subscribe((res) => {
        this.spinner.hide();
        if (res.total > 0) {
          this.swalService.confirmDialog(
            (swalRes) => {
              this.swalService.confirmDialog((swalRes) => {
                this.spinner.show();
                this.locationCompService
                  .removeWithAnnotations(id)
                  .pipe(takeUntil(this.unsubber.asObservable()))
                  .subscribe((res) => {
                    this.components.splice(key, 1);
                    this.changes++;
                    this.spinner.hide();
                    this.toastr
                      .info(this.undoMsg, '', {
                        progressBar: true,
                        timeOut: 7000,
                        enableHtml: true,
                      })
                      .onTap.pipe(take(1))
                      .subscribe(() => {
                        this.spinner.show();
                        this.locationCompService
                          .restoreWithAnnotations(id)
                          .subscribe((res) => {
                            this.components.splice(key, 0, res);
                            this.spinner.hide();
                          });
                      });
                  });
              });
            },
            {
              title: this._t.instant(
                'You are attempting to delete a component'
              ),
              text:
                this._t.instant('This component type is linked to') +
                ` ${res.total} ` +
                this._t.instant(
                  'annotation(s). By deleting this, all annotation linked to it will be deleted as well.'
                ),
            }
          );
        } else {
          this.swalService.confirmDialog((swalRes) => {
            this.spinner.show();
            this.locationCompService
              .remove(id)
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((res) => {
                this.components.splice(key, 1);
                this.changes++;
                this.spinner.hide();
                this.toastr
                  .info(this.undoMsg, '', {
                    progressBar: true,
                    timeOut: 7000,
                    enableHtml: true,
                  })
                  .onTap.pipe(take(1))
                  .subscribe(() => {
                    this.spinner.show();
                    this.locationCompService
                      .restoreWithAnnotations(id)
                      .subscribe((res) => {
                        this.components.splice(key, 0, res);
                        this.spinner.hide();
                      });
                  });
              });
          });
        }
      });
  }

  onDeleteLayer(id, key) {
    this.swalService.confirmDialog((swalRes) => {
      this.spinner.show();
      this.mapLayerService
        .remove(id)
        .pipe(takeUntil(this.unsubber.asObservable()))
        .subscribe((res) => {
          this.toastr
            .info(this.undoMsg, '', {
              progressBar: true,
              timeOut: 7000,
              enableHtml: true,
            })
            .onTap.pipe(take(1))
            .subscribe(() => {
              this.spinner.show();
              this.mapLayerService.restore(id).subscribe((res) => {
                this.components.splice(key, 0, res);
                this.auditTrailService.findAll(res.id).subscribe(
                  (res) => {
                    this.locationStore.currLocation$.value['auditTrails'] =
                      res.data;
                  },
                  ({ error }) => {}
                );
                if (res.layer_type === 'ortho') {
                  this.mapService.addOrtho(res, res.id);
                }
                if (res.layer_type === '3d') {
                  // this.mapService.loadSingle3DTilesDeckLayer(res.external_url);
                  this.potreeCesiumViewerService.unloadAllPointClouds();
                  this.potreeCesiumViewerService
                    .loadPointCloud(res.external_url)
                    .then((cloudjsData) => {
                      this.potreeCesiumViewerService.viewerIsActive$.next(true);
                    });
                }
                this.mapLayerService.getMapLayerTimeline(this.currLocation.id);
                this.mapLayers.push(res);
                this.changes++;
                this.spinner.hide();
              });
            });

          this.auditTrailService.findAll(res.id).subscribe(
            (res) => {
              this.locationStore.currLocation$.value['auditTrails'] = res.data;
            },
            ({ error }) => {}
          );
          this.changes++;
          this.spinner.hide();
          this.mapLayers.splice(key, 1);
          if (res && res.layer_type === 'ortho') {
            this.mapService.removeOrtho(this.currLocation.id);
          }
          if (res && res.layer_type === 'dsm') {
            this.mapService.removeDSM(this.currLocation.id);
          }
          if (res && res.layer_type === 'thermal') {
            this.mapService.removeThermal(this.currLocation.id);
          }
          if (res && res.layer_type === '3d-mesh') {
            this.mapService.removeThreeDMesh(this.currLocation.id);
          }
          if (res && res.layer_type === 'embed') {
            this.layerStore.isEmbed$.next(false);
            const layer = this.layerStore.embedLayers$.getValue();
            this.layerStore.embedLayers$.next(
              layer.reduce((acc, item) => {
                if (item._id !== id) [...acc, item];
              }, [])
            );
          }
          if (res && res.layer_type === 'geojson') {
            this.layerStore.isGeojson$.next(false);
            const layer = this.layerStore.geojsonLayers$.getValue();
            this.layerStore.geojsonLayers$.next(
              layer.reduce((acc, item) => {
                if (item._id !== id) [...acc, item];
              }, [])
            );
          }
          if (res && res.layer_type === '3d') {
            this.potreeCesiumViewerService.unloadAllPointClouds();
          }
          this.mapLayerService.getMapLayerTimeline(this.currLocation.id);
          // swalRes();
        });
    });
  }

  onSelect() {
    this.componentTypes = this.genericTypeStore.getGenericType(
      'component',
      false,
      false,
      true
    );

    if (this.currLocation?.generic_type_template?.component_set) {
      this.componentTypes = this.componentTypes.filter((ct) =>
        this.currLocation.generic_type_template.component_set.includes(ct.id)
      );
    }
  }

  async onSubmitForm(formName) {
    switch (formName) {
      case 'formComponent':
        if (this.formComponent.valid) {
          this.spinner.show();
          this.locationCompService
            .create(this.formComponent.value)
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              this.components.push(res);
              this.changes++;
              this.spinner.hide();
            });
        }
        break;
      case 'formMember':
        if (this.formMembers.valid && this.memberSelected) {
          this.spinner.show();
          if (this.isAllAssets) {
            if (this.currLocation?.children) {
              for (const child of this.currLocation?.children) {
                await this.locationService
                  .addMember(child.id, {
                    members: [this.memberSelected.id],
                  })
                  .toPromise();
              }
            }
          }
          this.locationService
            .addMember(this.currLocation.id, {
              members: [this.memberSelected.id],
            })
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              this.formMembers.patchValue({ members: '' });
              if (
                this.allMembers.filter(
                  (member) => member.id === this.memberSelected.id
                ).length == 0
              )
                this.allMembers.push(this.memberSelected);
              this.changes++;
              this.spinner.hide();
            });
        }
        break;
      case 'formReviewer':
        if (this.formReviewers.valid) {
          this.spinner.show();
          this.locationService
            .addReviewer(this.currLocation.id, {
              reviewers: [this.memberSelected.id],
            })
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              this.formReviewers.patchValue({ reviewers: '' });
              if (
                this.allReviewer.filter(
                  (member) => member.id === this.memberSelected.id
                ).length == 0
              )
                this.allReviewer.push(this.memberSelected);
              this.changes++;
              this.spinner.hide();
            });
        }
        break;
      case 'formSubmitter':
        if (this.formSubmitters.valid) {
          this.spinner.show();
          this.locationService
            .addSubmitter(this.currLocation.id, {
              submitter: [this.memberSelected.id],
            })
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              this.formSubmitters.patchValue({ submitter: '' });
              if (
                this.allSubmitter.filter(
                  (member) => member.id === this.memberSelected.id
                ).length == 0
              )
                this.allSubmitter.push(this.memberSelected);
              this.changes++;
              this.spinner.hide();
            });
        }
        break;
      case 'formMapLayer':
        if (this.formMapLayer.valid) {
          const value = this.formMapLayer.value;
          if (!value.layer_name) {
            value.layer_name = value.layer_type;
          }
          this.spinner.show();
          this.mapLayerService
            .create(this.formMapLayer.value)
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              // refresh history
              this.auditTrailService.findAll(res.id).subscribe(
                (res) => {
                  this.locationStore.currLocation$.value['auditTrails'] =
                    res.data;
                },
                ({ error }) => {}
              );
              if (res.layer_type === 'ortho') {
                this.mapService.addOrtho(res, res.id);
              }
              if (res.layer_type === '3d') {
                // this.mapService.loadSingle3DTilesDeckLayer(res.external_url);
                this.potreeCesiumViewerService.unloadAllPointClouds();
                this.potreeCesiumViewerService
                  .loadPointCloud(res.external_url)
                  .then((cloudjsData) => {
                    this.potreeCesiumViewerService.viewerIsActive$.next(true);
                  });
              }
              this.mapLayerService.getMapLayerTimeline(this.currLocation.id);
              this.mapLayers.push(res);
              this.changes++;
              this.spinner.hide();
            });
        }
        break;
    }
  }

  toggleForm(formName, stat?) {
    switch (formName) {
      case 'formComponent':
        this.isShowFormComp =
          typeof stat === 'undefined' ? !this.isShowFormComp : stat;
        if (!this.isShowFormComp)
          this.formComponent.patchValue({ name: '', component_type: '' });
        break;
      case 'formMapLayer':
        this.isShowFormMap =
          typeof stat === 'undefined' ? !this.isShowFormMap : stat;
        if (!this.isShowFormMap)
          this.formMapLayer.patchValue({ external_url: '', layer_type: '' });
        break;
      case 'formMember':
        this.isShowFormMember =
          typeof stat === 'undefined' ? !this.isShowFormMember : stat;
        if (!this.isShowFormMember)
          this.formMembers.patchValue({ members: '' });
        break;
      case 'formReviewer':
        this.isShowFormReviewer =
          typeof stat === 'undefined' ? !this.isShowFormReviewer : stat;
        if (!this.isShowFormReviewer)
          this.formReviewers.patchValue({ reviewers: '' });
        break;
      case 'formSubmitter':
        this.isShowFormSubmitter =
          typeof stat === 'undefined' ? !this.isShowFormSubmitter : stat;
        if (!this.isShowFormSubmitter)
          this.formSubmitters.patchValue({ submitter: '' });
        break;
    }
  }

  ngOnDestroy() {
    this.unsubber.next();
    this.unsubber.unsubscribe();
    if (this.changes) this.locUIService.refreshCurrLocation();
  }

  setMapScreenshot() {
    this.spinner.show();
    this.mapScreenshot = this.mapService.getMapScreenshot();
    const file = dataURLtoFile(
      this.mapScreenshot,
      `location_thumbnail_${
        this.locationStore.currentLocation.id
      }_${Date.now()}`
    );
    //upload arae_thumnbnail to s3
    const formData = new FormData();
    formData.append('location_thumbnail', file);
    formData.append('location', this.locationStore.currentLocation.id);

    this.fileService
      .create(formData)
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe(
        (createdFile) => {
          this.locationService
            .update(this.currLocation.id, {
              location_thumbnail: createdFile[0].url,
            })
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe(
              (res) => {
                this.spinner.hide();
              },
              (error) => {
                this.spinner.hide();
              }
            );
        },
        (error) => {
          this.spinner.hide();
        }
      );
  }

  onDeleteMembers(memberId, key, isWhat?: string) {
    this.swalService.confirmDialog((swalRes) => {
      this.spinner.show();
      switch (isWhat) {
        case 'isReviewer':
          this.locationService
            .removeReviewer(this.currLocation.id, { reviewers: [memberId] })
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              const deletedMember = this.allReviewer.splice(key, 1)[0];
              this.changes++;
              this.spinner.hide();
            });
          break;
        case 'isSubmitter':
          this.locationService
            .removeSubmitter(this.currLocation.id, { submitter: [memberId] })
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              const deletedMember = this.allSubmitter.splice(key, 1)[0];
              this.changes++;
              this.spinner.hide();
            });
          break;

        default:
          this.locationService
            .removeMember(this.currLocation.id, { members: [memberId] })
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              const deletedMember = this.allMembers.splice(key, 1)[0];
              this.changes++;
              this.spinner.hide();

              this.toastr
                .info(this.undoMsg, '', {
                  progressBar: true,
                  timeOut: 7000,
                  enableHtml: true,
                })
                .onTap.pipe(take(1))
                .subscribe(() => {
                  this.spinner.show();
                  this.locationService
                    .addMember(this.currLocation.id, {
                      members: [deletedMember.id],
                    })
                    .pipe(takeUntil(this.unsubber.asObservable()))
                    .subscribe((res) => {
                      this.formMembers.patchValue({ members: '' });
                      if (
                        this.allMembers.filter(
                          (member) => member.id === deletedMember.id
                        ).length == 0
                      )
                        this.allMembers.push(deletedMember);
                      this.changes++;
                      this.spinner.hide();
                    });
                });
              // swalRes();
            });
          break;
      }
    });
  }

  searchMember(keyword, includeGroup: boolean = true) {
    this.memberSelection = [];
    this.memberSelected = null;

    if (keyword.length > 0) {
      this.userService
        .findAll([
          { key: 'filtered[0][key]', val: 'fullname' },
          { key: 'filtered[0][val]', val: keyword },
          { key: 'filtered[0][type]', val: 'contains' },
          { key: 'limit', val: 3 },
          { key: 'select[0]', val: 'fullname' },
          { key: 'select[1]', val: 'id' },
        ])
        .pipe(takeUntil(this.unsubber.asObservable()))
        .subscribe((res) => {
          if (res.data.length > 0) {
            this.memberSelection = [].concat(
              this.memberSelection,
              res.data.map((i) => {
                return { name: i.fullname, id: i.id };
              })
            );
          }
        });
      if (includeGroup) {
        this.groupService
          .findAll([
            { key: 'filtered[0][key]', val: 'name' },
            { key: 'filtered[0][val]', val: keyword },
            { key: 'filtered[0][type]', val: 'contains' },
            { key: 'limit', val: 3 },
            { key: 'select[0]', val: 'name' },
            { key: 'select[1]', val: 'id' },
          ])
          .pipe(takeUntil(this.unsubber.asObservable()))
          .subscribe((res) => {
            if (res.data.length > 0) {
              this.memberSelection = [].concat(
                this.memberSelection,
                res.data.map((i) => {
                  return { name: i.name, id: i.id, isGroup: true };
                })
              );
            }
          });
      }
    }
  }

  selectedMember(data) {
    this.memberSelected = data;
    this.formMembers.patchValue({
      members: this.memberSelected.name,
    });
    this.memberSelection = [];
  }

  selectedReviewer(data) {
    this.memberSelected = data;
    this.formReviewers.patchValue({
      reviewers: this.memberSelected.name,
    });
    this.memberSelection = [];
  }

  selectedSubmitter(data) {
    this.memberSelected = data;
    this.formSubmitters.patchValue({
      submitters: this.memberSelected.name,
    });
    this.memberSelection = [];
  }

  /**
   * Handles onSelected from <app-file-upload>
   * @param evt FileList
   */
  onFileSelected(evt): void {
    const storeSelectedFiles = (files: FileList) => {
      this.ui.files = this.ui.files || [];

      for (let i = 0; i < evt.length; i++) {
        const file = evt.item(i);
        this.ui.files.push({
          file: file,
          name: file.name,
          uploadStatus: 'loaded',
        });
      }
    };

    storeSelectedFiles(evt);
  }

  async uploadFiles() {
    for (let i = 0; i < this.ui.files.length; i++) {
      this.ui.files[i].uploadStatus = 'queueing';
    }

    for (let i = 0; i < this.ui.files.length; i++) {
      this.progress = (i / this.ui.files.length) * 100;
      await this.uploadFile(i);
    }
    this.progress = -1;
  }

  async uploadFile(idx: number) {
    const file = this.ui.files[idx];
    file.uploadStatus = 'uploading';
    const folder = this.taskForm.value.folder;
    const formData = new FormData();
    formData.append('images', file.file);
    formData.append('asset_name', this.currLocation.name);
    if (folder) formData.append('folder', folder);

    return new Promise((resolve, reject) => {
      try {
        this.metadataService.upload(formData).subscribe(
          (res) => {
            file.uploadStatus = 'uploaded';
            return resolve('');
          },
          (error) => {
            file.uploadStatus = 'uploaded';
            return resolve('');
          }
        );
      } catch (error) {
        return reject();
      }
    });
  }

  /**
   * Removes file
   *
   * @param name
   * 'select' - files that hasn't been uploaded
   *
   * 'delete' - files that has been uploaded (requires API call)
   */
  removeFile(name: string, idx: number) {
    switch (name) {
      case 'select':
        {
          const files = this.ui.files;

          files.splice(idx, 1);
        }
        break;
      case 'delete':
        {
        }
        break;
    }
  }
}
